import { useUser } from "@clerk/clerk-expo";
import { events } from "@constants/tracking";
import { useUpdateUserProfile } from "@features/account/hooks/useUpdateUserProfile";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppLogo } from "@features/exam/hooks/useAppLogo";
import { useResumeSession } from "@features/session/hooks/useResumeSession";
import { PreferencesMenu } from "@features/settings/components/PreferencesMenu";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import useExams from "@hooks/useExams";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useTotalItemCount } from "@hooks/useTotalItemCount";
import type { AppContextCache } from "@memorang/constants/apps";
import type {
	OnboardingStep,
	OnboardingStepType,
} from "@memorang/constants/config/schema";
import { normalizeDate } from "@memorang/helpers/date";
import type { PracticeTestExam } from "@memorang/types";
import Loader from "@memorang/ui/components/Loader";
import { OnboardingApplet } from "@memorang/ui/components/OnboardingApplet";
import { OnboardingProvider } from "@memorang/ui/components/OnboardingApplet/OnboardingProvider";
import type {
	OnboardingCompletionDestination,
	OnboardingProfile,
	OnboardingSelections,
} from "@memorang/ui/components/OnboardingApplet/types";
import { useToast } from "@memorang/ui/components/Toast";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDiagnostic from "../../../containers/useDiagnostic";

const OnboardingRouteContainer = () => {
	const { step: stepFromUrl } = useLocalSearchParams<{
		step: OnboardingStepType;
	}>();
	const { t } = useTranslation();
	const { showToast } = useToast();
	const theme = useAppTheme();
	const { user } = useUser();
	const {
		username,
		firstName: authFirstName,
		lastName: authLastName,
		userInfo,
		isLoading,
		refetchUser,
	} = useAuthContext();
	const { saveProfile } = useUpdateUserProfile();
	const [profileError, setProfileError] = useState<string | undefined>();

	const onboardingSteps = useAppStore((s) => s.onboarding) ?? [];
	const appLogo = useAppLogo().default;

	const { data: exams, examsList, categories } = useExams();
	const { handleSwitchExamContext, updateExamDate, currentExamContext } =
		useExamContext();
	const totalItemCount = useTotalItemCount();
	const { data: practiceTestDistribution, isLoading: isDiagnosticsLoading } =
		useMixedItemDist({
			action: "GET_PRATICETEST_DISTRIBUTION",
		});
	const diagnostics = (practiceTestDistribution?.exams ?? []).filter(
		(exam) => exam.subType === "DIAGNOSTIC",
	);
	const {
		handleCreateSession: handleCreateDiagnosticSession,
		handleViewReport: handleViewDiagnosticReport,
		isCreatingSession,
		showErrorDialog,
		toggleShowErrorDialog,
	} = useDiagnostic();
	const { handleResumeSession } = useResumeSession();
	const [isCompleting, setIsCompleting] = useState(false);

	useEffect(() => {
		if (showErrorDialog) {
			showToast({ message: t("errors.sessionCreation.body") });
			toggleShowErrorDialog();
		}
	}, [showErrorDialog, showToast, t, toggleShowErrorDialog]);

	const { appContextCacheKey } = useCacheKey();
	const { setCache } = useCache<AppContextCache>({
		key: appContextCacheKey || "",
		defaultData: { selectedStates: [{ code: "", relatedExamsIds: [] }] },
		enabled: Boolean(appContextCacheKey),
		shouldOptimisticUpdate: true,
	});

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const router = useRouter();

	const startedAtRef = useRef(new Date().toISOString());
	const initialStepRef = useRef(
		stepFromUrl ?? onboardingSteps.find((s) => s.enabled)?.step,
	);

	useEffect(() => {
		if (initialStepRef.current) {
			trackCustomEvent({
				eventName: events.onboardingStepStarted,
				step: initialStepRef.current,
			});
		}
	}, []);

	const handleProfileChange = useCallback(
		async (profile: OnboardingProfile) => {
			setProfileError(undefined);
			const result = await saveProfile(profile);
			if (!result.ok) {
				setProfileError(result.error);
			}
		},
		[saveProfile],
	);

	const handleExamChange = useCallback(
		(id: string) => {
			const selected = examsList.find((e) => e.exam.id === id);
			if (selected) {
				handleSwitchExamContext(selected);
			}
		},
		[examsList, handleSwitchExamContext],
	);

	const handleStateChange = useCallback(
		(code: string) => {
			const relatedExam = examsList.find(
				(e) => e.exam.state?.code === code && e.exam.relatedExams,
			);
			setCache({
				selectedStates: [
					{
						code,
						relatedExamsIds: relatedExam?.exam.relatedExams ?? [],
					},
				],
			});
		},
		[examsList, setCache],
	);

	const handleStepChange = useCallback(
		({
			direction,
			from,
			to,
		}: {
			direction: "next" | "previous";
			from: OnboardingStep;
			to: OnboardingStep;
		}) => {
			router.setParams({ step: to.step });
			trackCustomEvent({
				eventName: events.onboardingStepStarted,
				step: to.step,
			});
			explicitCallTrackCustomEvent({
				eventName:
					direction === "next"
						? events.onboardingStepNavNext
						: events.onboardingStepNavPrev,
				step: from.step,
			});
			if (direction === "next") {
				explicitCallTrackCustomEvent({
					eventName: events.onboardingStepCompleted,
					step: from.step,
				});
			}
		},
		[router, explicitCallTrackCustomEvent],
	);

	const handleDiagnosticDestination = useCallback(
		async (exam: PracticeTestExam): Promise<boolean> => {
			if (exam.status === "ACTIVE" && exam.numericId != null) {
				return await handleResumeSession(exam.numericId);
			}
			if (exam.status === "COMPLETE" && exam.numericId != null) {
				handleViewDiagnosticReport(exam.numericId);
				return true;
			}
			return await handleCreateDiagnosticSession({
				exam,
				isSessionConfig: true,
				source: "onboarding",
			});
		},
		[
			handleCreateDiagnosticSession,
			handleResumeSession,
			handleViewDiagnosticReport,
		],
	);

	const handleComplete = useCallback(
		async (
			selections: OnboardingSelections,
			destination: OnboardingCompletionDestination,
		) => {
			if (isCompleting || isCreatingSession) {
				return;
			}
			setIsCompleting(true);
			const now = new Date().toISOString();
			const properties = {
				onboarding: {
					schema_version: 1,
					status: "completed",
					started_at: startedAtRef.current,
					updated_at: now,
					completed_at: now,
					profile: {
						username: selections.profile?.username,
						first_name: selections.profile?.firstName,
						last_name: selections.profile?.lastName,
						learner_type_id: selections.learnerTypeId,
						target_level_id: selections.rankId,
						skill_ids: selections.skillIds,
						plan_id: selections.planId,
						exam_id: selections.examId,
						category_id: selections.categoryId,
						level_id: selections.levelId,
						state_code: selections.stateCode,
					},
				},
			};
			try {
				const saveResult = await saveProfile({
					metadata: { properties },
				});
				if (!saveResult.ok) {
					setProfileError(saveResult.error);
					return;
				}

				// The mutation's cache write lands a render after we navigate, so the
				// protected-route guard would read the pre-save profile and bounce the
				// user straight back here. Refetching first settles the query before
				// any guarded route mounts.
				await refetchUser();
				if (destination.type === "diagnostic") {
					const succeeded = await handleDiagnosticDestination(
						destination.diagnostic,
					);
					if (!succeeded) {
						// Profile is already completed; leave onboarding rather than
						// trapping the user on the review screen with nowhere to go.
						router.replace("/home");
					}
					return;
				}
				router.replace("/home");
			} finally {
				setIsCompleting(false);
			}
		},
		[
			handleDiagnosticDestination,
			isCompleting,
			isCreatingSession,
			refetchUser,
			router,
			saveProfile,
		],
	);

	if (isLoading || !userInfo) {
		return <Loader />;
	}

	return (
		<OnboardingProvider
			steps={onboardingSteps}
			step={stepFromUrl}
			exams={exams}
			categories={categories}
			diagnostics={diagnostics}
			isCompletionPending={
				isDiagnosticsLoading || isCompleting || isCreatingSession
			}
			totalItemCount={totalItemCount}
			initialSelections={{
				examDate: normalizeDate(currentExamContext.examDate),
				profile: {
					username: username ?? "",
					firstName: authFirstName ?? user?.firstName ?? "",
					lastName: authLastName ?? user?.lastName ?? "",
				},
				learnerTypeId: userInfo.onboarding?.profile?.learner_type_id,
				rankId: userInfo.onboarding?.profile?.target_level_id,
				skillIds: userInfo.onboarding?.profile?.skill_ids,
			}}
			errors={{ profile: profileError }}
			onProfileChange={handleProfileChange}
			onExamChange={handleExamChange}
			onExamDateChange={(date) => updateExamDate(date)}
			onStateChange={handleStateChange}
			onStepChange={handleStepChange}
			onComplete={handleComplete}
		>
			<OnboardingApplet
				logo={{ light: appLogo.light, dark: appLogo.dark }}
				renderHeaderRight={() => (
					<PreferencesMenu iconColor={theme.colors.onPrimary} />
				)}
			/>
		</OnboardingProvider>
	);
};

export default OnboardingRouteContainer;
